import { Skeleton, Stack, Typography } from '@atgfr8/atg-ui-tailwind';
import { searchItems } from '@components/Header/search/constants';
import { loadSchema } from '@components/Header/search/validation';
import { ToastType } from '@components/Toast';
import {
  MOBILE_CATEGORIES,
  SearchBodyProps,
  SearchCategoryInternal,
  SearchFieldProps,
} from '@common/types';
import { useNavigate } from '@solidjs/router';
import { findOrder } from '@store/Global/service';
import { resetLtlQuoteState } from '@store/ltl/store';
import {
  resetCarrierStore,
  resetCustomerStore,
  setOrderStore,
  TabType,
} from '@store/orders';
import { userStore } from '@store/user';
import { routeOrderDetails } from '@utils/routeOrderDetails';
import { handleToast } from '@utils/utils';
import { compact } from 'lodash';
import { createMemo, createSignal, Show } from 'solid-js';
import { ValidationError } from 'yup';
import { setDashboardStore } from '@mobileStore/global/store';

import { SearchBody } from './SearchBody';

const SearchErrorBanner = (props: { message: string }) => (
  <div
    class="flex items-start gap-2 rounded border border-red-400 px-3 py-2"
    style={{ background: '#FDEDED' }}
  >
    <span class="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold">
      !
    </span>
    <div>
      <p class="text-sm font-semibold text-red-700">{props.message}</p>
      <p class="text-xs text-black">Please try with a different keyword</p>
    </div>
  </div>
);

export const SearchField = (props: SearchFieldProps) => {
  const isMobile = props.variant === 'mobile';

  const [selectedCategory, setSelectedCategory] =
    createSignal<SearchCategoryInternal>('Load');
  const [text, setText] = createSignal<string | undefined>();
  const [clearSearch, setClearSearch] = createSignal(false);
  const [isLoading, setIsLoading] = createSignal(false);
  const [searchError, setSearchError] = createSignal<string | null>(null);

  const navigate = useNavigate();

  const handleChange = (value: string) => {
    setSelectedCategory(
      value === 'Reference #' ? 'Ref #' : (value as SearchCategoryInternal),
    );
    setText(undefined);
    setDashboardStore('globalSearchHasError', false);
    setSearchError(null);
    setClearSearch(true);
  };

  const handleLoadSearch = async () => {
    const loadNumber = Number(text());
    if (!loadNumber) return;

    setIsLoading(true);
    setDashboardStore('globalSearchHasError', false);
    setSearchError(null);

    if (!isMobile) {
      resetLtlQuoteState();
      resetCustomerStore();
      resetCarrierStore();
      setOrderStore('quoteId', undefined);
      setOrderStore('activeTab', { index: 0, type: TabType.load });
    }

    try {
      loadSchema.validateSync(loadNumber, { abortEarly: false });
      const data = await findOrder(loadNumber);

      if (data) {
        if (isMobile) {
          navigate(`/Order/Details/${loadNumber}`);
          props.onClose?.();
        } else {
          const isNative = routeOrderDetails(loadNumber);
          if (isNative) navigate(`/Order/Details/${loadNumber}`);
        }
      } else if (isMobile) {
        setSearchError('No Results Found');
        setDashboardStore('globalSearchHasError', true);
      }
    } catch (err) {
      if (isMobile) {
        setDashboardStore('globalSearchHasError', true);
        setSearchError(
          err instanceof ValidationError
            ? 'Min 6 digits required'
            : 'No Results Found',
        );
      } else {
        const errors = err instanceof ValidationError ? err.errors : [];
        handleToast(ToastType.Error, errors.join(', '));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const menuItems = createMemo(() =>
    compact(
      searchItems
        .filter(({ label }) => !isMobile || MOBILE_CATEGORIES.includes(label))
        .map(({ label, icon, disclaimer, show }) =>
          show()
            ? {
                value: label,
                label: (
                  <div class="flex min-h-[40px] w-full flex-row items-center gap-2">
                    <img
                      src={icon}
                      class="h-7.5 w-7.5 shrink-0 object-contain"
                    />
                    <Stack spacing={0}>
                      <Typography
                        variant="body2"
                        class="font-medium! text-[13px]! leading-tight! text-[#333333] group-hover:text-white!"
                      >
                        {label}
                      </Typography>
                      <Typography
                        variant="caption"
                        class="text-[11px]! leading-tight! text-[#999999] group-hover:text-white!"
                      >
                        {disclaimer}
                      </Typography>
                    </Stack>
                  </div>
                ),
              }
            : null,
        ),
    ),
  );

  const bodyProps: SearchBodyProps = {
    isMobile,
    selectedCategory,
    text,
    setText,
    clearSearch,
    setClearSearch,
    isLoading,
    setSearchError,
    menuItems,
    handleChange,
    handleLoadSearch,
    onClose: props.onClose,
  };

  if (isMobile) {
    return (
      <div
        class={`px-3 pb-3 pt-2 bg-white ${props.isHome ? 'rounded-b-2xl shadow-md' : ''}`}
      >
        <div class="flex flex-col gap-1">
          <SearchBody {...bodyProps} />
          <Show when={searchError()}>
            {(message) => <SearchErrorBanner message={message()} />}
          </Show>
        </div>
      </div>
    );
  }

  return (
    <Show
      when={!userStore.isLoading}
      fallback={<Skeleton variant="rectangular" height={50} width={240} />}
    >
      <SearchBody {...bodyProps} />
    </Show>
  );
};
